import React from 'react';
import {
  Box,
  Container,
  useColorModeValue,
  useToast,
  useBreakpointValue
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { OnboardingService } from '../../../services/onboardingService';
import logger from '../../../utils/logger';

// Import extracted components
import Header from './Header';
import ProgressCard from './ProgressCard';
import ChatInterface from './ChatInterface';
import WordCollection from './WordCollection';
import ContinueButton from './ContinueButton';
import { useChatLogic } from './ChatLogic';

interface VocabularyBuilderProps {
  onNext: (data?: any) => void;
  onPrevious: () => void;
  stepData: Record<string, any>;
  isLoading: boolean;
}

const VocabularyBuilder: React.FC<VocabularyBuilderProps> = ({
  onNext,
  stepData,
  isLoading
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();

  // Responsive values
  const containerMaxW = useBreakpointValue({ base: 'container.md', lg: 'container.lg' });

  // Gaming-inspired theme colors
  const bgGradient = useColorModeValue(
    'linear(to-br, gray.50, blue.50, purple.50)',
    'linear(to-br, gray.900, blue.900, purple.900)'
  );

  // Debug logging to understand what's being passed
  console.log('VocabularyBuilder stepData:', stepData);
  console.log('Language goals:', stepData.languageGoals);

  // Fix: Ensure correct languages are used (don't hardcode incorrect values)
  const targetLanguage = stepData.languageGoals?.target_language || 'dutch';
  const nativeLanguage = stepData.languageGoals?.native_language || 'english';
  const assessmentLevel = stepData.assessmentResults?.level_determined || 'beginner_low';

  console.log('Using targetLanguage:', targetLanguage);
  console.log('Using nativeLanguage:', nativeLanguage);

  // Initialize chat logic
  const {
    messages,
    currentMessage,
    setCurrentMessage,
    selectedWords,
    isAgentTyping,
    chatPhase,
    sendMessage,
    addWordToCollection
  } = useChatLogic({
    targetLanguage,
    nativeLanguage,
    initialSelectedWords: stepData.selectedVocabulary || []
  });

  const progressPercentage = Math.min((selectedWords.length / 20) * 100, 100);

  const handleNext = async () => {
    if (!user) return;

    try {
      // Complete the onboarding step
      await OnboardingService.saveVocabularySelections(user.id, selectedWords, targetLanguage, nativeLanguage);
      onNext({ selectedVocabulary: selectedWords });
    } catch (error) {
      logger.error('Failed to save vocabulary selections:', error);
      toast({
        title: t('onboarding.vocabulary.saveError'),
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  return (
    <Box minH="100vh" bgGradient={bgGradient}>
      <Container maxW={containerMaxW} py={8}>
        {/* Header Component */}
        <Header />

        {/* Progress Card */}
        <Box mt={8}>
          <ProgressCard
            selectedWordsCount={selectedWords.length}
            progressPercentage={progressPercentage}
          />
        </Box>

        {/* Chat Interface */}
        <Box mt={8}>
          <ChatInterface
            messages={messages}
            currentMessage={currentMessage}
            setCurrentMessage={setCurrentMessage}
            sendMessage={sendMessage}
            isAgentTyping={isAgentTyping}
            onWordSelect={addWordToCollection}
          />
        </Box>

        {/* Word Collection Summary */}
        {selectedWords.length > 0 && (
          <Box mt={8}>
            <WordCollection selectedWords={selectedWords} />
          </Box>
        )}

        {/* Continue Button */}
        <Box mt={8}>
          <ContinueButton
            onClick={handleNext}
            isLoading={isLoading}
            isDisabled={selectedWords.length < 5}
            selectedWordsCount={selectedWords.length}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default VocabularyBuilder;